import { ComponentType } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest } from 'rxjs';
import { ChatChannelBrowseDialogComponent } from '../dialogs/chat-channel-browse-dialog/chat-channel-browse-dialog.component';
import { ChatChannelCreateDialogComponent } from '../dialogs/chat-channel-create-dialog/chat-channel-create-dialog.component';
import { ChatDirectCreateDialogComponent } from '../dialogs/chat-direct-create-dialog/chat-direct-create-dialog.component';
import { ChatFavoriteSectionDialogComponent } from '../dialogs/chat-favorite-section-dialog/chat-favorite-section-dialog.component';
import { ChatPreferencesDialogComponent } from '../chat-preferences/chat-preferences-dialog.component';
import { activeChannelIdSelector, directDraftSelector } from '../state/chat.selectors';
import { ChatDialogService } from './chat-dialog.service';

/** What a create dialog produced — exactly one of the two is set. */
export interface ChatCreatedConversation {
  /** An existing or newly created conversation. */
  channelId?: string;
  /** A direct conversation that does not exist yet (draft). */
  directUserId?: string;
}

/**
 * The creation and discovery dialogs every chat header and list call —
 * SPEC-2026-09-internal-chat-pwa (T-24). The product's bubble and the chat app's
 * shell open them through here, so neither keeps its own copy of the adoption rule.
 *
 * The create dialogs answer `close(true)` — never the new id — because
 * CHANNEL_CREATE's ack carries none. They put the result in the STORE instead, by
 * two routes and at two different times:
 *
 * - a direct conversation: the dialog dispatches `openConversation` (it already
 *   existed) or `openDirectDraft` (it does not), BEFORE closing;
 * - a channel or group: nobody knows the id until the `created` socket fan-out
 *   arrives, which the state bridge turns into `openConversation` — AFTER the
 *   dialog has closed.
 *
 * So instead of racing `afterClosed()`, a flag is raised while a create dialog is in
 * flight and the next selection the store reports is published on `created$`. The
 * flag drops when the dialog is dismissed, so a cancelled creation never steals a
 * later selection.
 */
@Injectable({ providedIn: 'root' })
export class ChatConversationDialogsService {

  private readonly createdSubject = new Subject<ChatCreatedConversation>();

  /** One emission per adopted creation. Never completes. */
  readonly created$: Observable<ChatCreatedConversation> = this.createdSubject.asObservable();

  private adoptNextSelection = false;

  constructor(
    private readonly dialog: ChatDialogService,
    private readonly store: Store,
  ) {
    combineLatest([
      this.store.select(activeChannelIdSelector),
      this.store.select(directDraftSelector),
    ]).subscribe(([activeChannelId, directDraft]) => {
      if (!this.adoptNextSelection) return;
      if (activeChannelId) {
        this.adoptNextSelection = false;
        this.createdSubject.next({ channelId: activeChannelId });
      } else if (directDraft?.userId) {
        this.adoptNextSelection = false;
        this.createdSubject.next({ directUserId: directDraft.userId });
      }
    });
  }

  openNewDirect(): void {
    this.openCreateDialog(ChatDirectCreateDialogComponent);
  }

  openNewChannel(): void {
    this.openCreateDialog(ChatChannelCreateDialogComponent);
  }

  /**
   * Public-channel directory — browse and join. Not a creation: joining dispatches
   * its own bootstrap reload and the channel simply appears under "Canais".
   */
  openBrowseChannels(): void {
    this.dialog.open(ChatChannelBrowseDialogComponent, { width: '560px' });
  }

  /** FAVORITE_SECTION_UPSERT without a sectionId — creates a custom section. */
  openNewSection(): void {
    this.dialog.open(ChatFavoriteSectionDialogComponent, { width: '420px', data: {} });
  }

  /** Global notification preferences — quiet hours and sound. */
  openPreferences(): void {
    this.dialog.open(ChatPreferencesDialogComponent, { width: '480px' });
  }

  private openCreateDialog(component: ComponentType<unknown>): void {
    this.adoptNextSelection = true;
    this.dialog
      .open(component, { width: '480px' })
      .afterClosed()
      .subscribe((created) => {
        if (created !== true) this.adoptNextSelection = false;
      });
  }
}
